
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
// No longer need 'User' from firebase/auth as we get the token result from context
import { defaultNavigationModules, defaultNavigationSubModules, navigationData } from '../navigation.data';

export type UserRoles = {
  [roleName: string]: string[] | boolean;
};

export const useUserRoles = () => {
  // Now consumes the comprehensive loading state and the new 'claims' object from AuthContext
  const { user, userProfile, loading, claims } = useAuth();
  const [roles, setRoles] = useState<UserRoles>({});
  
  // The isRolesLoading state is now directly tied to the loading state from AuthContext
  const [isRolesLoading, setIsRolesLoading] = useState(true);

  useEffect(() => {
    // If the main auth context is loading, this hook is also loading.
    if (loading) {
      setIsRolesLoading(true);
      return;
    }
    // If there's no user, there are no roles.
    if (!user) {
      setRoles({});
      setIsRolesLoading(false);
      return;
    }

    // This function now processes the claims from context AND the user profile from Firestore.
    const processRoles = () => {
      try {
        const claimRoles = (claims?.roles as UserRoles) || {};
        const profileRoles = (userProfile?.roles as unknown as UserRoles) || {};
        
        // DEBUG LOGGING - UNCOMMENTED FOR DEBUGGING
        // console.log('[useUserRoles] user:', user.email);
        // console.log('[useUserRoles] claimRoles:', claimRoles);
        // console.log('[useUserRoles] profileRoles:', profileRoles);

        // Merge roles from both sources. 
        const mergedRoles: UserRoles = { ...claimRoles };

        Object.keys(profileRoles).forEach(key => {
            const profileValue = profileRoles[key];
            const claimValue = mergedRoles[key];

            if (claimValue === undefined) {
                // If it's not in claims, take it from profile
                mergedRoles[key] = profileValue;
            } else if (typeof profileValue === 'boolean' && typeof claimValue === 'boolean') {
                // If both are boolean, OR them (true wins)
                mergedRoles[key] = profileValue || claimValue;
            } 
        });

        // console.log('[useUserRoles] mergedRoles:', mergedRoles);
        setRoles(mergedRoles);

      } catch (error) {
        console.error("Error processing user roles:", error);
        setRoles({});
      } finally {
        // Once processing is done, the roles are loaded.
        setIsRolesLoading(false);
      }
    };

    processRoles();

  }, [user, loading, claims, userProfile]);

  // isSuperAdmin should be a strict boolean check for clarity and security.
  const isSuperAdmin = roles.super_admin === true;
  const isCourseAdmin = roles.course_admin === true;

  const navigationModules = useMemo(() => {
    if (isRolesLoading) return [];
    
    // Super admins always get everything
    if (isSuperAdmin) return navigationData.map((item: any) => item.navId);
    
    // Fallback to defaults if navigationModules is missing or empty
    const profileModules = userProfile?.navigationModules;
    const modules = profileModules && profileModules.length > 0 ? profileModules : defaultNavigationModules;
    
    // BACKWARD COMPATIBILITY: Map old module IDs to new ones
    return modules.map(m => {
      if (m === 'console') return 'dashboard';
      if (m === 'academy') return 'skills';
      if (m === 'assistant') return 'assist';
      return m;
    });
  }, [isRolesLoading, isSuperAdmin, userProfile?.navigationModules]);

  const navigationSubModules = useMemo(() => {
    if (isRolesLoading) return [];
    
    // Super admins always get everything
    if (isSuperAdmin) {
        return navigationData
            .flatMap((item: any) => item.subItems || [])
            .map((subItem: any) => subItem.navId)
            .filter((navId): navId is string => !!navId);
    }
    
    // Fallback to defaults if navigationSubModules is missing or empty
    const profileSubModules = userProfile?.navigationSubModules;
    const subModules = profileSubModules && profileSubModules.length > 0 ? profileSubModules : defaultNavigationSubModules;

    // BACKWARD COMPATIBILITY: Map old sub-module IDs to new ones
    const mapped = subModules.map(m => {
      if (m === 'course-creation') return 'course-management';
      return m;
    });

    if ((roles.studio_admin || roles.content_admin) && !mapped.includes('news-management')) {
      return [...mapped, 'news-management'];
    }

    return mapped;
  }, [isRolesLoading, isSuperAdmin, roles.studio_admin, roles.content_admin, userProfile?.navigationSubModules]);
  
  // hasAdminRole checks if the user has any administrative role.
  const hasAdminRole = isSuperAdmin || Object.keys(roles).some(key => {
    if (key === 'super_admin') return false;
    const value = roles[key];
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return !!value;
  });
  
  const hasMarketingRole = Object.keys(roles).some(role => role.startsWith('marketing'));
  const hasAcademyAdminRole = Array.isArray(roles.academy_admin) && roles.academy_admin.length > 0;

  /**
   * Checks if the user has a specific role and, optionally, an attribute for that role.
   */
  const can = (role: string, attribute?: string): boolean => {
    // A super admin can do anything, regardless of other roles.
    if (isSuperAdmin) return true;
    
    const assignedAttributes = roles[role];

    if (!assignedAttributes) return false;
    
    if (Array.isArray(assignedAttributes)) {
        if (!attribute) return assignedAttributes.length > 0;
        return assignedAttributes.includes(attribute);
    }
    
    return !!assignedAttributes;
  };

  return { 
    roles, 
    isRolesLoading, 
    hasAdminRole, 
    isSuperAdmin,
    isCourseAdmin,
    can, 
    hasMarketingRole,
    hasAcademyAdminRole,
    navigationModules, 
    navigationSubModules 
  };
};
